<script setup lang="ts">
import {ref, watch} from 'vue'
import {Loader2, Pencil, Trash2, X, Check} from 'lucide-vue-next'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {Badge} from '@/components/ui/badge'
import {ScrollArea} from '@/components/ui/scroll-area'
import {Label} from '@/components/ui/label'
import {useToast} from '@/composables/useToast'
import {useAppStore} from '@/stores/app'
import type {Alarm} from '@backend/types'

const open = defineModel<boolean>('open', {required: true})

const appStore = useAppStore()
const toast = useToast()

const loading = ref(false)
const alarms = ref<Alarm[]>([])
const editingId = ref<number | null>(null)
const editNote = ref('')
const editTriggerAt = ref('')
const savingId = ref<number | null>(null)
const deleteTarget = ref<Alarm | null>(null)
const deleteDialogOpen = ref(false)
const deletingId = ref<number | null>(null)

watch(open, async (isOpen) => {
  if (!isOpen || !appStore.apiService) return

  loading.value = true
  editingId.value = null
  try {
    alarms.value = await appStore.apiService.getAlarms()
  } catch {
    toast.error('Не удалось загрузить будильники')
  } finally {
    loading.value = false
  }
})

function toLocalDatetime(date: string | Date) {
  const d = new Date(date)
  const offset = d.getTimezoneOffset()
  const local = new Date(d.getTime() - offset * 60000)
  return local.toISOString().slice(0, 16)
}

function startEdit(alarm: Alarm) {
  editingId.value = alarm.alarm_id
  editNote.value = alarm.note
  editTriggerAt.value = toLocalDatetime(alarm.trigger_at)
}

function cancelEdit() {
  editingId.value = null
}

async function saveEdit(alarm: Alarm) {
  if (!appStore.apiService) return

  savingId.value = alarm.alarm_id
  try {
    const updated = await appStore.apiService.updateAlarm(alarm.alarm_id, {
      note: editNote.value,
      trigger_at: new Date(editTriggerAt.value).toISOString(),
    })
    const idx = alarms.value.findIndex((a) => a.alarm_id === alarm.alarm_id)
    if (idx !== -1) alarms.value[idx] = updated
    editingId.value = null
    toast.success('Будильник обновлён')
  } catch {
    toast.error('Не удалось обновить')
  } finally {
    savingId.value = null
  }
}

async function confirmDelete() {
  if (!appStore.apiService || !deleteTarget.value) return

  const id = deleteTarget.value.alarm_id
  deletingId.value = id
  try {
    await appStore.apiService.deleteAlarm(id)
    alarms.value = alarms.value.filter((a) => a.alarm_id !== id)
    toast.success('Будильник удалён')
  } catch {
    toast.error('Не удалось удалить')
  } finally {
    deletingId.value = null
    deleteTarget.value = null
    deleteDialogOpen.value = false
  }
}

function startDelete(alarm: Alarm) {
  deleteTarget.value = alarm
  deleteDialogOpen.value = true
}

function formatDatetime(date: string | Date) {
  return new Date(date).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function isPast(date: string | Date) {
  return new Date(date) < new Date()
}
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent class="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Будильники Дживса</DialogTitle>
        <DialogDescription>Напоминания и будильники, настроенные через Telegram</DialogDescription>
      </DialogHeader>

      <div v-if="loading" class="flex items-center justify-center py-8">
        <Loader2 class="size-6 animate-spin text-muted-foreground" />
      </div>

      <div v-else-if="alarms.length === 0" class="py-8 text-center text-sm text-muted-foreground">
        Нет активных будильников. Попросите Дживса поставить напоминание в Telegram-чате.
      </div>

      <ScrollArea v-else class="max-h-[60vh]">
        <div class="space-y-3 pr-3">
          <div v-for="alarm in alarms" :key="alarm.alarm_id" class="rounded-lg border p-3 text-sm">
            <template v-if="editingId === alarm.alarm_id">
              <div class="space-y-2">
                <div>
                  <Label class="mb-1 text-xs">Заметка</Label>
                  <Textarea v-model="editNote" class="min-h-[60px]" @keydown.ctrl.enter="saveEdit(alarm)" />
                </div>
                <div>
                  <Label class="mb-1 text-xs">Время срабатывания</Label>
                  <Input v-model="editTriggerAt" type="datetime-local" />
                </div>
                <div class="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" @click="cancelEdit" :disabled="savingId === alarm.alarm_id">
                    <X class="mr-1 size-3.5" />
                    Отмена
                  </Button>
                  <Button size="sm" @click="saveEdit(alarm)" :disabled="savingId === alarm.alarm_id">
                    <Loader2 v-if="savingId === alarm.alarm_id" class="mr-1 size-3.5 animate-spin" />
                    <Check v-else class="mr-1 size-3.5" />
                    Сохранить
                  </Button>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="mb-1.5 whitespace-pre-wrap">{{ alarm.note }}</div>
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span
                    class="text-xs"
                    :class="isPast(alarm.trigger_at) ? 'text-destructive' : 'text-muted-foreground'">
                    {{ formatDatetime(alarm.trigger_at) }}
                  </span>
                  <Badge v-if="alarm.cron_schedule" variant="secondary" class="text-[10px] px-1.5 py-0">
                    {{ alarm.cron_schedule }}
                  </Badge>
                </div>
                <div class="flex gap-1">
                  <Button variant="ghost" size="icon" class="size-7" @click="startEdit(alarm)">
                    <Pencil class="size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="size-7 text-destructive hover:text-destructive"
                    aria-label="Удалить будильник"
                    @click="startDelete(alarm)">
                    <Trash2 class="size-3.5" />
                  </Button>
                </div>
              </div>
            </template>
          </div>
        </div>
      </ScrollArea>
    </DialogContent>
  </Dialog>

  <AlertDialog v-model:open="deleteDialogOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Удалить будильник?</AlertDialogTitle>
        <AlertDialogDescription> Будильник будет удалён безвозвратно и больше не сработает. </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel :disabled="!!deletingId" @click="deleteTarget = null">Отмена</AlertDialogCancel>
        <Button variant="destructive" :disabled="!!deletingId" @click="confirmDelete">
          <Loader2 v-if="deletingId" class="mr-2 size-4 animate-spin" />
          Удалить
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
